import { useEffect, useState, type ReactNode } from 'react';
import { useOutboxSync } from '../capture/useOutboxSync';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { appCopy } from '@zibanu/shared';
import { AppHeader, Banner, BottomTabBar, IconButton, SideNav, type TabId } from '@zibanu/ui';
import { useDeps } from './deps';
import { useBooks, useMe, useOffers } from './session';
import { useBooksQuery } from './data';
import { stamp } from './format';
import { todayIn } from './entries';
import { useOfflineSince } from './offline';
import { CaptureProvider, useCapture } from '../capture/CaptureProvider';

/** 1024 px and up: the sidebar (spec 3A §6.6). */
export function useIsDesktop(): boolean {
  const query = '(min-width: 1024px)';
  const [desktop, setDesktop] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const changed = () => setDesktop(mq.matches);
    mq.addEventListener('change', changed);
    return () => mq.removeEventListener('change', changed);
  }, []);
  return desktop;
}

const TAB_PATHS = { home: '/', records: '/records', customers: '/customers', more: '/more' } as const;

function tabOf(path: string): TabId {
  if (path.startsWith('/records')) return 'records';
  if (path.startsWith('/customers')) return 'customers';
  if (path.startsWith('/more')) return 'more';
  return 'home';
}

function Frame({ children }: { children: ReactNode }) {
  useOutboxSync();
  const since = useOfflineSince();
  const me = useMe().data;
  const { current, choose } = useBooks();
  const { platform } = useDeps();
  const mayCapture = useOffers()('capture.record');
  const capture = useCapture();
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const desktop = useIsDesktop();
  const alerts = useBooksQuery('alerts.list', { limit: 30 });
  // Signed out this very moment: the gate is about to replace this screen.
  if (!me) return null;
  const go = (tab: TabId) => {
    if (tab === 'capture') capture.open();
    else void navigate({ to: TAB_PATHS[tab] });
  };
  const header = (
    <AppHeader
      variant={desktop ? 'desktop' : 'mobile'}
      orgs={me.memberships.map((m) => ({ id: m.orgId, name: m.label, role: appCopy.role(m.role) }))}
      {...(current ? { org: current.orgId } : {})}
      onOrgChange={choose}
      onAddBusiness={platform.openWhatsApp}
      actions={
        <>
          <IconButton icon="search" label={appCopy.searchRecords()} onClick={() => void navigate({ to: '/records', search: { find: true } })} />
          <IconButton icon="bell" label={appCopy.alerts()} badge={alerts.data?.unseenCount || undefined} onClick={() => void navigate({ to: '/alerts' })} />
        </>
      }
    />
  );
  return (
    <div className={desktop ? 'zw-shell zw-shell--desktop' : 'zw-shell'}>
      {desktop && (
        <SideNav
          active={tabOf(path)}
          items={[
            { id: 'home', label: appCopy.tabHome(), icon: 'house' },
            { id: 'records', label: appCopy.tabRecords(), icon: 'receipt-text' },
            { id: 'customers', label: appCopy.tabCustomers(), icon: 'users-round' },
            { id: 'more', label: appCopy.tabMore(), icon: 'ellipsis' },
          ]}
          onChange={(id) => go(id as TabId)}
          {...(mayCapture ? { onCapture: capture.open } : {})}
          captureCount={capture.waiting}
        />
      )}
      <div className="zw-shell__main">
        {header}
        <main className="zw-shell__content">
          {since !== null &&
            (since > 0 && current ? (
              <Banner tone="offline" title={appCopy.offlineReading(stamp(new Date(since).toISOString(), current.timezone, todayIn(current.timezone, platform.now())))} />
            ) : (
              <Banner tone="offline" title={appCopy.offlineTitle()}>
                {appCopy.offlineText()}
              </Banner>
            ))}
          {children}
        </main>
      </div>
      {!desktop && <BottomTabBar active={tabOf(path)} onChange={go} badges={{ capture: capture.waiting }} showCapture={mayCapture} />}
    </div>
  );
}

/** Every screen behind the gate: header, the books, and how you move (spec 3A §6.1, §6.6). */
export function AppShell({ children }: { children: ReactNode }) {
  return (
    <CaptureProvider>
      <Frame>{children}</Frame>
    </CaptureProvider>
  );
}
